import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../common/Icon';
import { useSiteSettings } from '../../context/SiteSettingsContext';

/**
 * LogoBlock
 *
 * Single source of truth for rendering the brand (logo or text fallback)
 * across every layout: public Header, DashboardLayout Header, AdminLayout
 * sidebar, mobile drawer, etc.
 *
 * Behaviour:
 *  - Reads logo_url + site_name from SiteSettingsContext.
 *  - If logo_url is present, renders the uploaded image.
 *  - Otherwise renders an accent-colored icon tile + site_name text.
 *  - Never hardcodes the brand name or logo path.
 *
 * Variants:
 *  - "public" (visitor header): h-12 logo, sm:h-16 with wider limits.
 *  - "default" (dashboard header): h-8 logo, site_name visible on
 *    sm+ screens.
 *  - "compact" (admin sidebar header): h-8 logo, site_name always visible
 *    next to a small subtitle ("Admin Panel" by default).
 *  - "drawer" (mobile slide-in drawer): h-8 logo, site_name always visible.
 *
 * Props:
 *  - variant      : "default" | "public" | "compact" | "drawer"
 *  - to           : link destination (default "/" — pass null/"" to render
 *                   without a surrounding <Link>).
 *  - subtitle     : small muted text under the site_name (compact variant).
 *  - onClick      : forwarded to the link (useful for closing mobile drawers).
 *  - className    : extra classes on the outer element.
 *  - testId       : data-testid on the outer element.
 */
export default function LogoBlock({
  variant = 'default',
  to = '/',
  subtitle,
  onClick,
  className = '',
  testId = 'logo-block',
}) {
  const { logo_url, site_name } = useSiteSettings();
  const brand = site_name || 'SnappiPit';

  const isPublic = variant === 'public';
  const isCompact = variant === 'compact';
  const isDrawer = variant === 'drawer';

  // Image + text block (the visual payload)
  const visual = logo_url ? (
    <img
      src={logo_url}
      alt={brand}
      className={
        isCompact
          ? 'h-8 w-auto max-w-[140px] object-contain'
          : isDrawer
            ? 'h-8 w-auto max-w-[140px] object-contain'
            : isPublic
              ? 'h-12 sm:h-16 w-auto max-w-[160px] sm:max-w-[240px] object-contain'
              : 'h-8 w-auto max-w-[160px] object-contain'
      }
      data-testid="logo-block-img"
    />
  ) : (
    <>
      <div
        className={
          isCompact
            ? 'w-8 h-8 rounded-lg bg-accent flex items-center justify-center shrink-0'
            : 'w-8 h-8 bg-accent rounded-sm flex items-center justify-center shrink-0'
        }
        data-testid="logo-block-fallback-icon"
      >
        <Icon name="car" className="h-5 w-5 text-white" />
      </div>
      {isCompact || isDrawer ? (
        <div className="min-w-0">
          <h1
            className={
              isCompact
                ? 'font-heading font-bold text-lg leading-tight truncate'
                : 'font-heading font-bold text-lg truncate'
            }
            data-testid="logo-block-name"
          >
            {brand}
          </h1>
          {isCompact && subtitle ? (
            <p className="text-xs text-muted-foreground truncate" data-testid="logo-block-subtitle">
              {subtitle}
            </p>
          ) : null}
        </div>
      ) : (
        <span
          className="font-heading font-bold text-xl tracking-tight hidden sm:block"
          data-testid="logo-block-name"
        >
          {brand}
        </span>
      )}
    </>
  );

  // If an uploaded logo is present AND we're in a variant that also wants
  // a subtitle (admin sidebar), show the subtitle next to the image so the
  // "Admin Panel" label is preserved even when a custom logo is uploaded.
  const visualWithCompactSubtitle =
    logo_url && isCompact && subtitle ? (
      <div className="flex items-center gap-2 min-w-0">
        {visual}
        <p className="text-xs text-muted-foreground truncate" data-testid="logo-block-subtitle">
          {subtitle}
        </p>
      </div>
    ) : (
      visual
    );

  const wrapperClass = `flex items-center gap-2 ${className}`.trim();

  if (!to) {
    return (
      <div className={wrapperClass} data-testid={testId}>
        {visualWithCompactSubtitle}
      </div>
    );
  }

  return (
    <Link to={to} onClick={onClick} className={wrapperClass} data-testid={testId}>
      {visualWithCompactSubtitle}
    </Link>
  );
}
